import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/shared/auth/store';
import { refreshAccess } from '@/shared/api/auth';
import { BottomNav, type BottomNavItem } from '@/shared/ui/BottomNav';
import { Icon } from '@/shared/ui/Icon';
import { ToastHost } from '@/shared/ui/Toast';
import { usePushRegistration } from '@/features/push/usePushRegistration';

const PUBLIC_PATH_PREFIXES = ['/login', '/invite', '/auth/callback'];

// 인증된 사용자에게 푸시(FCM)를 조용히 연결 — 권한이 이미 있으면 토큰 재등록,
// 포그라운드 메시지 토스트. 훅 내부에서 미인증·미지원·플래그 off 시 no-op.
function PushBootstrap() {
  usePushRegistration();
  return null;
}

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

// 모임방은 홈의 섹션으로 통합 — 첫 사용자가 '모임 vs 모임방'을 고르지 않게 탭은 둘만 둔다
const NAV_ITEMS: BottomNavItem[] = [
  { to: '/home', icon: 'home', label: '홈' },
  { to: '/profile', icon: 'user', label: '마이' },
];

// 서브 화면의 결정적 상위 경로 — PWA standalone에는 브라우저 뒤로가기가 없다
function backTargetOf(pathname: string, search = ''): string | null {
  const placesSub = pathname.match(/^(\/meetings\/[^/]+)\/places/);
  if (placesSub) {
    const params = new URLSearchParams(search);
    const from = params.get('from');
    if (params.get('for') === 'meeting-point' || from === 'vote') return `${placesSub[1]}/vote`;
    if (from === 'chat') return `${placesSub[1]}/chat`;
    if (from === 'live') return `${placesSub[1]}/live`;
    return placesSub[1];
  }
  const pointSub = pathname.match(/^(\/meetings\/[^/]+)\/point/);
  if (pointSub) return pointSub[1];
  const meetingSub = pathname.match(/^(\/meetings\/[^/]+)\/.+/);
  if (meetingSub) return meetingSub[1];
  if (/^\/meetings\/[^/]+$/.test(pathname)) return '/home';
  if (/^\/rooms\/[^/]+/.test(pathname)) return '/rooms';
  if (pathname === '/rooms') return '/home';
  if (
    pathname === '/expense-suggest' ||
    pathname === '/transfer-suggest' ||
    pathname.startsWith('/pay/')
  ) {
    return '/home';
  }
  return null;
}

// 서브 화면 제목을 상단 바에 통합 (네이티브 내비 바) — 페이지 내 큰 헤딩 제거로 수직 공간 절약
const SUB_TITLES: Array<[RegExp, string]> = [
  [/^\/meetings\/[^/]+\/vote/, '투표'],
  [/^\/meetings\/[^/]+\/chat/, '대화'],
  [/^\/meetings\/[^/]+\/expense/, '정산'],
  [/^\/meetings\/[^/]+\/settlement/, '정산'],
  [/^\/meetings\/[^/]+\/places/, '진행'],
  [/^\/meetings\/[^/]+\/point/, '집결지'],
  [/^\/meetings\/[^/]+\/schedule\/[^/]+/, '일정'],
  [/^\/meetings\/[^/]+\/live/, 'LIVE'],
  [/^\/meetings\/[^/]+$/, '모임'],
  [/^\/rooms\/[^/]+/, '모임방'],
  [/^\/rooms$/, '모임방'],
];

function titleOf(pathname: string): string | null {
  for (const [re, title] of SUB_TITLES) {
    if (re.test(pathname)) return title;
  }
  return null;
}

export default function App() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setToken = useAuthStore((s) => s.setToken);
  const clearAuth = useAuthStore((s) => s.logout);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [sessionRestored, setSessionRestored] = useState(false);

  // C안: persist한 user가 있지만 accessToken이 없는 첫 로드라면
  // refresh 쿠키로 선제 access 재발급. 공개 경로에서는 불필요.
  useEffect(() => {
    let cancelled = false;
    if (user && !accessToken && !isPublicPath(pathname)) {
      refreshAccess()
        .then((token) => {
          if (!cancelled) setToken(token);
        })
        .catch(() => {
          if (cancelled) return;
          clearAuth();
          navigate('/login', { replace: true });
        })
        .finally(() => {
          if (!cancelled) setSessionRestored(true);
        });
    } else {
      setSessionRestored(true);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBottomNav = user != null && !isPublicPath(pathname);
  const backTo = user != null && !isPublicPath(pathname) ? backTargetOf(pathname, search) : null;
  const barTitle = backTo ? titleOf(pathname) : null;
  const restoring = user != null && !accessToken && !isPublicPath(pathname) && !sessionRestored;

  return (
    <div className="flex min-h-full flex-col bg-transparent">
      {/* v7: 플로팅 글래스 필 헤더 — 캔버스 오로라 위에 떠 있다 */}
      <header className="sticky top-0 z-30 px-4 pb-1 pt-[max(env(safe-area-inset-top),10px)]">
        <div className="mx-auto flex max-w-screen-sm items-center justify-between gap-2 rounded-full border border-surface-border/80 bg-surface/85 py-1.5 pl-2 pr-4 shadow-[var(--shadow-card)] backdrop-blur-xl">
          <div className="flex min-w-0 items-center gap-1">
            {backTo ? (
              <button
                type="button"
                onClick={() => navigate(backTo)}
                aria-label="뒤로"
                className="pressable flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-muted text-ink hover:bg-surface-pressed focus-visible:outline-none focus-visible:shadow-focus-ring"
              >
                <Icon name="back" size={20} />
              </button>
            ) : (
              <span
                aria-hidden="true"
                className="ml-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-[image:var(--hero-grad)] text-[12px] font-bold text-white"
              >
                모
              </span>
            )}
            {barTitle ? (
              <span className="ml-1.5 truncate text-[16px] font-semibold leading-none tracking-[-0.3px] text-ink">
                {barTitle}
              </span>
            ) : (
              <Link
                to="/home"
                className="ml-1.5 text-[17px] font-semibold leading-none tracking-[-0.4px] text-ink focus-visible:outline-none focus-visible:shadow-focus-ring"
                aria-label="모이자 홈"
              >
                모이자
              </Link>
            )}
          </div>
          {!user && pathname !== '/login' ? (
            <Link
              to="/login"
              className="shrink-0 rounded-full bg-primary px-3.5 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-primary-strong"
            >
              로그인
            </Link>
          ) : null}
        </div>
      </header>
      {/* pb는 한쪽만 — pb-7과 pb-[calc(…)]를 같이 두면 CSS 순서에 따라 작은 값이 이겨
          고정 바텀 내비가 마지막 콘텐츠를 가린다 */}
      <main
        className={`mx-auto w-full max-w-screen-sm flex-1 px-5 ${
          showBottomNav ? 'pb-[calc(94px+env(safe-area-inset-bottom))]' : 'pb-7'
        }`}
      >
        {restoring ? (
          <p
            className="py-12 text-center text-body text-ink-muted"
            role="status"
            aria-live="polite"
          >
            세션을 복원하는 중…
          </p>
        ) : (
          <Outlet />
        )}
      </main>
      {!showBottomNav ? (
        <footer className="border-t border-surface-border/60 py-4 text-center text-small text-ink-muted">
          모이자
        </footer>
      ) : null}
      {showBottomNav ? <BottomNav items={NAV_ITEMS} /> : null}
      <PushBootstrap />
      <ToastHost />
    </div>
  );
}
